/**
 * Slack Message Control for JsonForms
 * A custom control that uses Lexical editor to edit Slack markdown messages
 */

import { cn } from "@/utils";
import type { ControlProps } from "@jsonforms/core";
import { optionIs, RankedTester, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
} from "@lexical/markdown";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import type { EditorState, LexicalEditor } from "lexical";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { SLACK_TRANSFORMERS } from "./SlackMessageControl/slackTransformers";

import "prismjs";
import { SlackToolbar } from "./SlackMessageControl/Toolbar";

const SlackMessageControlComponent: React.FC<ControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
  visible,
  id,
}) => {
  const [editor, setEditor] = useState<LexicalEditor | null>(null);
  const isInitializedRef = useRef(false);
  const lastEmittedDataRef = useRef<any>(null);

  const readonly = !enabled;

  // Create editor configuration
  const initialConfig = {
    namespace: "slack-markdown-editor",
    onError: (error: Error) => {
      console.error("Lexical editor error:", error);
    },
    editable: !readonly,
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      CodeNode,
      CodeHighlightNode,
      LinkNode,
    ],
    theme: {
      paragraph: "slack-editor-paragraph",
      quote: "slack-editor-quote",
      list: {
        ul: "slack-editor-list-ul",
        ol: "slack-editor-list-ol",
        listitem: "slack-editor-listitem",
      },
      code: "slack-editor-code",
      text: {
        bold: "slack-editor-text-bold",
        italic: "slack-editor-text-italic",
        strikethrough: "slack-editor-text-strikethrough",
        underline: "slack-editor-text-underline",
        code: "slack-editor-text-code",
      },
    },
  };

  // Handle editor changes - convert to Slack markdown
  const onChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        let markdown = $convertToMarkdownString(SLACK_TRANSFORMERS);

        // Unescape characters that Slack doesn't require escaped
        // Lexical may still escape these in certain contexts
        markdown = markdown
          .replace(/\\_/g, "_") // Unescape underscores (for emojis like :pirate_flag:)
          .replace(/\\~/g, "~"); // Unescape tildes (shouldn't be needed but just in case)

        // Store the markdown we're about to emit
        lastEmittedDataRef.current = markdown;

        handleChange(path, markdown);
      });
    },
    [handleChange, path],
  );

  // Initialize editor with markdown data only once
  useEffect(() => {
    if (editor && !isInitializedRef.current) {
      isInitializedRef.current = true;

      const markdownText = typeof data === "string" ? data : "";

      editor.update(() => {
        $convertFromMarkdownString(markdownText, SLACK_TRANSFORMERS);
      });

      lastEmittedDataRef.current = data;
    }
  }, [editor, data]);

  // Update editor when data changes externally (not from our own onChange)
  useEffect(() => {
    if (editor && isInitializedRef.current) {
      // Check if the data change came from an external source
      const dataChanged =
        typeof data === "string" && data !== lastEmittedDataRef.current;

      if (dataChanged) {
        const markdownText = data;

        editor.update(() => {
          $convertFromMarkdownString(markdownText, SLACK_TRANSFORMERS);
        });

        lastEmittedDataRef.current = data;
      }
    }
  }, [data, editor]);

  if (!visible) return null;

  return (
    <div className="mb-4" id={id}>
      <label className="text-sm font-medium mb-2 block">
        {label || "Message"}
      </label>
      <div
        className={cn(
          "border rounded-lg overflow-hidden",
          "border-neutral-200 dark:border-neutral-800",
          "bg-n-00 dark:bg-n-10",
        )}
      >
        <LexicalComposer
          initialConfig={{
            ...initialConfig,
            editorState: undefined, // We'll set it manually
          }}
        >
          <EditorRefSetter setEditor={setEditor} />
          <SlackToolbar readonly={readonly} />
          <div className="relative p-4">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  className={cn(
                    "slack-editor-content",
                    "min-h-[150px]",
                    "focus:outline-none",
                    "prose prose-sm max-w-none",
                    "dark:prose-invert",
                  )}
                />
              }
              placeholder={
                <div className="slack-editor-placeholder absolute top-4 left-4 text-neutral-400 pointer-events-none">
                  Type your message... (Use * for *bold*, _ for _italic_, ~ for
                  ~strikethrough~)
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <OnChangePlugin onChange={onChange} />
            <HistoryPlugin />
            <ListPlugin />
            <MarkdownShortcutPlugin transformers={SLACK_TRANSFORMERS} />
          </div>
        </LexicalComposer>
      </div>
      {readonly && (
        <p className="text-xs text-muted-foreground mt-2">
          This message content is read-only.
        </p>
      )}
      <style>{`
        .slack-editor-content .slack-editor-paragraph {
          margin: 0.5em 0;
        }

        .slack-editor-content .slack-editor-quote {
          margin: 0.5em 0;
          padding-left: 1em;
          border-left: 3px solid #d1d5db;
          font-style: italic;
        }

        .dark .slack-editor-content .slack-editor-quote {
          border-left-color: #4b5563;
        }

        .slack-editor-content .slack-editor-list-ul,
        .slack-editor-content .slack-editor-list-ol {
          margin: 0.5em 0;
          padding-left: 1.5em;
        }

        .slack-editor-content .slack-editor-list-ul {
          list-style-type: disc;
        }

        .slack-editor-content .slack-editor-list-ol {
          list-style-type: decimal;
        }

        .slack-editor-content .slack-editor-listitem {
          margin: 0.25em 0;
        }

        .slack-editor-content .slack-editor-code {
          background-color: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          padding: 1em;
          margin: 0.5em 0;
          font-family: monospace;
          font-size: 0.9em;
          overflow-x: auto;
          white-space: pre-wrap;
          display: block;
        }

        .dark .slack-editor-content .slack-editor-code {
          background-color: #1f2937;
          border-color: #4b5563;
        }

        .slack-editor-content .slack-editor-text-bold {
          font-weight: bold;
        }

        .slack-editor-content .slack-editor-text-italic {
          font-style: italic;
        }

        .slack-editor-content .slack-editor-text-strikethrough {
          text-decoration: line-through;
        }

        .slack-editor-content .slack-editor-text-underline {
          text-decoration: underline;
        }

        .slack-editor-content .slack-editor-text-code {
          background-color: #f3f4f6;
          border-radius: 3px;
          padding: 0.1em 0.3em;
          font-family: monospace;
          font-size: 0.9em;
        }

        .dark .slack-editor-content .slack-editor-text-code {
          background-color: #374151;
        }
      `}</style>
    </div>
  );
};

/**
 * Helper component to get editor reference
 */
function EditorRefSetter({
  setEditor,
}: {
  setEditor: (editor: LexicalEditor) => void;
}) {
  const [editorInstance] = useLexicalComposerContext();

  useEffect(() => {
    setEditor(editorInstance);
  }, [editorInstance, setEditor]);

  return null;
}

/**
 * Tester function that determines if this control should be used.
 * Matches when the UI schema has the option { control: "slack_markdown_editor" }
 *
 * To use this control, add the following to your UI schema:
 * {
 *   "type": "Control",
 *   "scope": "#/properties/text",
 *   "options": {
 *     "control": "slack_markdown_editor"
 *   }
 * }
 */
export const slackMarkdownEditorTester: RankedTester = rankWith(
  5,
  optionIs("control", "slack_markdown_editor"),
);

export const SlackMessageControl = withJsonFormsControlProps(
  SlackMessageControlComponent,
);
